// ---------------------------------------------------------------------------
// The two things a first sign-in has to ask, in the order they matter.
//
//   1. A username, because /api/me answers `username: null` for a verified user
//      who has not claimed one and nothing else can choose it for them.
//   2. Whether to adopt the guest games already recorded against this device.
//
// Both are asked in one small dialog rather than on a screen of their own,
// because someone who has just signed in was on their way somewhere else and
// this should not read as a checkpoint. Both have a way out. The claim is only
// ever offered — it is never made without a press, and a decline is remembered
// against the device so it is not asked twice.
//
// The username rules are not restated here. `validateUsername` from shared is
// the same function the server calls, so an obvious mistake gets an instant
// answer in exactly the server's words, and anything the server alone can know
// — a name already taken, a change inside the 30-day cooldown — arrives as the
// server's own sentence and is shown verbatim.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { USERNAME_MAX, validateUsername } from '@countdown/shared';
import { useAccount } from '../../hooks/useAccount.js';
import { useGameStore } from '../../store/gameStore.js';
import { usePlayerStore } from '../../store/playerStore.js';
import { Button } from '../primitives/Button.js';
import { useUsernamePrompt } from './usernamePrompt.js';

const DECLINED_KEY = 'countdown-claim-declined';

/** Remembered per device, because the games being offered are this device's. */
function readDeclined(deviceId: string): boolean {
  try {
    return localStorage.getItem(DECLINED_KEY) === deviceId;
  } catch {
    return false;
  }
}

function rememberDeclined(deviceId: string): void {
  try {
    localStorage.setItem(DECLINED_KEY, deviceId);
  } catch {
    // A browser that refuses storage just gets asked again next time.
  }
}

function reasonFrom(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Could not reach the server.';
}

export function AccountOnboarding() {
  const account = useAccount();
  const deviceId = usePlayerStore((store) => store.deviceId);
  const phase = useGameStore((store) => store.state?.phase ?? null);

  // Asking again is a bump of this, from the header menu. See usernamePrompt.ts.
  const nonce = useUsernamePrompt((store) => store.nonce);
  const [postponedAt, setPostponedAt] = useState<number | null>(null);
  const [declined, setDeclined] = useState(() => readDeclined(deviceId));
  const [adopted, setAdopted] = useState<number | null>(null);

  const details = account.account;
  if (!account.enabled || !account.ready || !account.signedIn || details === null) return null;
  if (postponedAt === nonce) return null;

  // Never over a live round. Somebody who reloads mid-game arrives here with a
  // username still to pick, and a dialog across the board would cost them the
  // thirty seconds it covered. The lobby and the end of the game are the two
  // places in a room where there is nothing to interrupt.
  if (phase !== null && phase !== 'lobby' && phase !== 'game-over') return null;

  if (details.username === null) {
    return (
      <Dialog title="Pick a username">
        <UsernameForm onLater={() => setPostponedAt(nonce)} />
      </Dialog>
    );
  }

  if (adopted !== null) {
    return (
      <Dialog title="Games added">
        <p className="text-[15px] leading-relaxed text-ink-muted">
          {adopted === 1
            ? 'One game on this device is now on your account.'
            : `${adopted} games on this device are now on your account.`}
        </p>
        <div className="mt-5 flex justify-end">
          <Button variant="primary" size="md" onClick={() => setAdopted(null)}>
            Done
          </Button>
        </div>
      </Dialog>
    );
  }

  if (details.claimableGames > 0 && !declined) {
    return (
      <Dialog title="Games on this device">
        <ClaimOffer
          count={details.claimableGames}
          onDecline={() => {
            rememberDeclined(deviceId);
            setDeclined(true);
          }}
          onAdopted={setAdopted}
        />
      </Dialog>
    );
  }

  return null;
}

function Dialog({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
      style={{ background: 'var(--c-scrim)' }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="sheet-up w-full max-w-[420px] rounded-frame border border-line bg-surface p-5"
      >
        <h2 className="text-[22px] leading-tight font-bold tracking-[-0.01em]">{title}</h2>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

function UsernameForm({ onLater }: { onLater: () => void }) {
  const account = useAccount();
  const displayName = usePlayerStore((store) => store.displayName);

  const [value, setValue] = useState('');
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const check = validateUsername(value);
    if (!check.ok) {
      setReason(check.error);
      return;
    }

    setReason(null);
    setBusy(true);
    try {
      // The display name they already play under travels with the claim, so the
      // account starts out as the person the room already knows.
      await account.chooseUsername(check.value, displayName.trim() || undefined);
    } catch (caught) {
      setReason(reasonFrom(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <p className="text-[15px] leading-relaxed text-ink-muted">
        It is permanent, it is how other players find you, and it sits beside your name so nobody
        can pass themselves off as you.
      </p>

      <div className="flex flex-col gap-2">
        <label htmlFor="username" className="label-mono">
          username
        </label>
        <div className="flex items-center gap-2 rounded-[11px] border border-line bg-surface-2 px-3">
          <span aria-hidden="true" className="font-mono text-[17px] text-ink-dim">
            @
          </span>
          <input
            id="username"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            maxLength={USERNAME_MAX}
            autoComplete="username"
            spellCheck={false}
            autoCapitalize="none"
            className="min-h-12 flex-1 bg-transparent font-mono text-[17px] text-ink placeholder:text-ink-dim"
            placeholder="isaac"
          />
        </div>
      </div>

      {reason !== null && (
        <p role="alert" className="text-[14px] leading-relaxed text-alert">
          {reason}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="md" onClick={onLater} disabled={busy}>
          Later
        </Button>
        <Button variant="primary" size="md" type="submit" disabled={busy || value.length === 0}>
          {busy ? 'Saving' : 'Take this username'}
        </Button>
      </div>
    </form>
  );
}

function ClaimOffer({
  count,
  onDecline,
  onAdopted,
}: {
  count: number;
  onDecline: () => void;
  onAdopted: (claimed: number) => void;
}) {
  const account = useAccount();
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function adopt() {
    setReason(null);
    setBusy(true);
    try {
      onAdopted(await account.adoptGuestGames());
    } catch (caught) {
      setReason(reasonFrom(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[15px] leading-relaxed text-ink-muted">
        {count === 1
          ? 'You have 1 game on this device. Add it to your account?'
          : `You have ${count} games on this device. Add them to your account?`}
      </p>

      {reason !== null && (
        <p role="alert" className="text-[14px] leading-relaxed text-alert">
          {reason}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="md" onClick={onDecline} disabled={busy}>
          No thanks
        </Button>
        <Button variant="primary" size="md" onClick={() => void adopt()} disabled={busy}>
          {busy ? 'Adding' : count === 1 ? 'Add it' : 'Add them'}
        </Button>
      </div>
    </div>
  );
}
